import { core } from '@icc/shared';
import { _, Common, parseDate } from '@icc/shared/helpers';
import { Currency, exchange, ExchangeRate, formatCurrency } from '@icc/shared/currency';
import { PriceModifier } from '../interfaces/price-modifier.interface';
import { TaxRate } from '../interfaces/tax-rate.interface';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { SalesDocument } from '../models/sales-document.model';
import { TaxRatesService } from './tax-rates.service';
import { Configuration } from '@icc/configurator/shared';
import { Discount, PricePart } from '@icc/shared/types';
import { Promotion } from '../interfaces/promotion.interface';
import { endOfDay } from 'date-fns';

export class CalculateDiscountsAndMultipliersService {
    // eslint-disable-next-line max-statements
    static calculateDiscountsAndMultipliers(
        {
            document,
            position,
            type,
            discounts,
            salesMultipliers,
            charge,
            licenseType,
            exchangeRates,
            buyExchangeRates,
            taxRate,
            canExceedMaxDiscount,
            promotions,
        }: {
            document: SalesDocument;
            position: SalesDocumentPosition;
            type: 'buy' | 'sales';
            discounts: PriceModifier[],
            salesMultipliers: PriceModifier[],
            charge: number;
            licenseType: string;
            exchangeRates: ExchangeRate[];
            buyExchangeRates: ExchangeRate[];
            taxRate?: TaxRate;
            canExceedMaxDiscount?: boolean;
            promotions: Promotion[];
        }
    ) {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        let mainDiscount = null;
        let accessoriesDiscount = null;
        let maxDiscountExceeded = false;

        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        if (position.type === 'complex') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'Configurator.ComplexConfigurators' &&
                    el.foreignKey === Number(position.configuration.configuratorId) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));

        } else if (position.type === 'static') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'Product.Products' &&
                    el.foreignKey === Number(position.configuration.productId) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));
        } else if (position.type === 'external_venetian') {
            mainDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'ExternalVenetian.ShutterTypes' &&
                    el.foreignKey === Number(position.configuration.shutterType?.id) &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));
            accessoriesDiscount = core.copy(discounts.find(
                (el) =>
                    el.model === 'ConfigurationFeatures.WindowAccessories' &&
                    (!el.dateFrom || el.dateFrom <= date) &&
                    (!el.dateTo || el.dateTo >= date)
            ));
        }
        const mainPromotion = this.getPositionPromotion(position, promotions);

        const positionPrice = position.basePriceValue;

        position.priceBeforePromotionsValue = TaxRatesService.recalcPrice(
            TaxRatesService.isGross(document.taxRateCalculationMethod || 'netSummary'),
                exchange(positionPrice, position.basePriceCurrency, document.priceCurrency || 'PLN', type === 'buy' ? buyExchangeRates : exchangeRates),
            position.basePriceIsGross,
            position.basePriceTaxRate,
            taxRate?.value ?? position.priceTaxRate
        );

        let promotion = 0;
        let promotionValue = 0;
        let promotionBaseValue = 0;

        if (mainPromotion && mainPromotion.type === 'percent') {
            promotion = mainPromotion.value;
            promotionValue = core.round(position.priceBeforePromotionsValue * mainPromotion.value / 100);
            promotionBaseValue = exchange(promotionValue, document.priceCurrency || 'PLN', position.basePriceCurrency, type === 'buy' ? buyExchangeRates : exchangeRates);
        }

        position.pricePromotions = [{
            name: mainPromotion?.name || _('SALES_DOCUMENT|Promocja'),
            basePrice: exchange(position.priceBeforePromotionsValue, document.priceCurrency || 'PLN', position.basePriceCurrency, type === 'buy' ? buyExchangeRates : exchangeRates),
            basePriceCurrency: position.basePriceCurrency,
            price: position.priceBeforePromotionsValue,
            promotion,
            baseValue: promotionBaseValue,
            value: promotionValue,
            promotionType: 'percent',
            external: false,
        }];

        if (position.priceBeforePromotionsValue !== null) {
            position.priceBeforeDiscountsValue = position.pricePromotions.reduce(
                (prev, curr) => prev - curr.value,
                position.priceBeforePromotionsValue,
            );
        } else {
            position.priceBeforeDiscountsValue = null;
        }

        if (position.type === 'external_venetian') {
            const { positionDiscounts, maxDiscountExceeded: confMaxDiscountExceeded, priceAfterDiscountsValue } = this.updatePositionConfiguration(position.configuration, mainDiscount, accessoriesDiscount, {
                document,
                position,
                side: type,
                exchangeRates,
                buyExchangeRates,
                taxRate,
                canExceedMaxDiscount,
                promotion: mainPromotion,
            });
            position.priceDiscounts = positionDiscounts;
            maxDiscountExceeded = maxDiscountExceeded || confMaxDiscountExceeded;
            if (position.priceBeforeDiscountsValue !== null) {
                position.priceAfterDiscountsValue = priceAfterDiscountsValue;
            } else {
                position.priceAfterDiscountsValue = null;
            }
        } else{
            let discount = 0;
            let value = 0;
            let baseValue = 0;

            if (mainDiscount) {
                if (mainDiscount?.type === 'percent') {
                    discount = mainDiscount.value;
                    if (document.discountType === 'price') {
                        value = core.round(position.priceBeforeDiscountsValue * mainDiscount.value / 100);
                    } else {
                        value = core.round(core.round(position.priceBeforeDiscountsValue * position.quantity) * mainDiscount.value / 100);
                    }
                    baseValue = exchange(value, document.priceCurrency || 'PLN', position.basePriceCurrency, type === 'buy' ? buyExchangeRates : exchangeRates);
                } else {
                    value = exchange(mainDiscount.value, position.basePriceCurrency, document.priceCurrency || 'PLN', type === 'buy' ? buyExchangeRates : exchangeRates);
                    if (document.discountType === 'price') {
                        discount = core.round(value / position.priceBeforeDiscountsValue * 100);
                    } else {
                        discount = core.round(value / (position.priceBeforeDiscountsValue * position.quantity) * 100);
                    }
                }
            }
            const price = position.priceBeforeDiscountsValue *
                    (document.discountType === 'price'
                        ? 1
                        : position.quantity);
            position.priceDiscounts = position.priceDiscounts.filter(el => el.edited).map(el => {
                el.basePrice = exchange(price, document.priceCurrency || 'PLN', el.basePriceCurrency, type === 'buy' ? buyExchangeRates : exchangeRates);
                el.price = price;
                el.maxDiscount = mainDiscount?.maxValue;
                el.maxDiscountType = mainDiscount?.type ?? 'value';
                if (el.discountType === 'percent') {
                    if (el.maxDiscount !== null && el.discount > el.maxDiscount && !el.external) {
                        if (!canExceedMaxDiscount) {
                            el.discount = el.maxDiscount;
                        }
                        maxDiscountExceeded = true;
                    }
                    if (document.discountType === 'price') {
                        el.value = core.round(position.priceBeforeDiscountsValue * el.discount / 100);
                    } else {
                        el.value = core.round(core.round(position.priceBeforeDiscountsValue * position.quantity) * el.discount / 100);
                    }
                    el.baseValue = exchange(el.value, document.priceCurrency || 'PLN', el.basePriceCurrency, type === 'buy' ? buyExchangeRates : exchangeRates);
                } else {
                    if (el.maxDiscount !== null && el.baseValue > el.maxDiscount && !el.external) {
                        if (!canExceedMaxDiscount) {
                            el.baseValue = el.maxDiscount;
                        }
                        maxDiscountExceeded = true;
                    }
                    el.value = exchange(el.baseValue, el.basePriceCurrency, document.priceCurrency || 'PLN', type === 'buy' ? buyExchangeRates : exchangeRates);
                    if (document.discountType === 'price') {
                        el.discount = core.round(el.value / position.priceBeforeDiscountsValue * 100);
                    } else {
                        el.discount = core.round(el.value / (position.priceBeforeDiscountsValue * position.quantity) * 100);
                    }
                }
                return el;
            });
            if (!position.priceDiscounts.some(el => el.type === 'main')) {
                position.priceDiscounts.push({
                    name: _('SALES_DOCUMENT|Rabat główny'),
                    basePrice: exchange(price, document.priceCurrency || 'PLN', position.basePriceCurrency, type === 'buy' ? buyExchangeRates : exchangeRates),
                    basePriceCurrency: position.basePriceCurrency,
                    price,
                    discount,
                    maxDiscount: mainDiscount?.maxValue,
                    maxDiscountType: mainDiscount?.type ?? 'value',
                    baseValue,
                    value,
                    type: 'main',
                    discountType: mainDiscount?.type ?? 'value',
                    edited: false,
                    external: false,
                });
            }
            if (position.priceBeforeDiscountsValue !== null) {
                position.priceAfterDiscountsValue = position.priceDiscounts.reduce(
                    (prev, curr) => prev - curr.value,
                    position.priceBeforeDiscountsValue *
                        (document.discountType === 'price'
                            ? 1
                            : position.quantity)
                );
            } else {
                position.priceAfterDiscountsValue = null;
            }
        }

        return { maxDiscountExceeded };
    }

    static getMultiplier(model: string, foreignKey: string | number, salesMultipliers: PriceModifier[]): number {
        const multiplier = salesMultipliers.find(
            (el) =>
                el.model === model &&
                el.foreignKey === Number(foreignKey)
        );

        return multiplier?.value || 1;
    }

    static getSalesPrice(value: number, position: SalesDocumentPosition, charge: number, marketFactor: number, salesMultipliers: PriceModifier[]) {
        if (value === null) {
            return null;
        }
        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        let multiplier = 1;
        if (position.type === 'complex') {
            multiplier = this.getMultiplier('Configurator.ComplexConfigurators', Number(position.configuration.configuratorId), salesMultipliers) || 1;
        } else if (position.type === 'static') {
            multiplier = this.getMultiplier('Product.Products', Number(position.configuration.productId), salesMultipliers) || 1;
        }
        return value * multiplier * charge * marketFactor;
    }

    static getPositionMultiplier(position: SalesDocumentPosition, salesMultipliers: PriceModifier[]) {
        if (!Common.isObject(position.configuration)) {
            position.configuration = core.parseJson(position.configuration);
        }
        let multiplier = 1;
        if (position.type === 'complex') {
            multiplier = this.getMultiplier('Configurator.ComplexConfigurators', Number(position.configuration.configuratorId), salesMultipliers) || 1;
        } else if (position.type === 'static') {
            multiplier = this.getMultiplier('Product.Products', Number(position.configuration.productId), salesMultipliers) || 1;
        }
        return multiplier;
    }

    static getPositionPromotion(position: SalesDocumentPosition, promotions: Promotion[]): Promotion | undefined {
        const matchingPromotions = promotions.filter(el => {
            if (el.dateFrom && parseDate(el.dateFrom) > new Date()) {
                return false;
            }
            if (el.dateTo && endOfDay(parseDate(el.dateTo)) < new Date()) {
                return false;
            }

            if (position.type === 'static') {
                return el.model === 'Products' && (!el.record.length || el.record.map(Number).includes(+position.configuration?.productId));
            }

            if (position.type === 'complex') {
                return el.model === 'ComplexConfigurators' && (!el.record.length || el.record.map(Number).includes(+position.configuration?.configuratorId));
            }
            return false;
        }).sort((a, b) => b.priority - a.priority) // sort by priority descending;

        return matchingPromotions[0];
    }

    static updatePositionConfiguration(configuration: Configuration, mainDiscount, accessoriesDiscount, {
        document,
        position,
        side,
        exchangeRates,
        buyExchangeRates,
        taxRate,
        canExceedMaxDiscount,
        promotion,
    }: {
        document: SalesDocument;
        position: SalesDocumentPosition;
        side: 'buy' | 'sales';
        exchangeRates: ExchangeRate[];
        buyExchangeRates: ExchangeRate[];
        taxRate?: TaxRate;
        canExceedMaxDiscount?: boolean;
        promotion: Promotion
    }) {
        const positionsDiscounts: Record<string, Discount> = {
            main: {
                name: _('SALES_DOCUMENT|Rabat główny'),
                type: 'main',
                baseUnitPrices: [],
                basePrice: 0,
                basePriceCurrency: position.basePriceCurrency,
                price: 0,
                value: 0,
                discount: 0,
                maxDiscount: mainDiscount?.maxValue,
                maxDiscountType: mainDiscount?.type ?? 'percent',
                baseValue: 0,
                discountType: mainDiscount?.type ?? 'percent',
                edited: false,
                external: false,
            },
            accessories: {
                name: _('SALES_DOCUMENT|Rabat za akcesoria'),
                type: 'accessories',
                basePrice: 0,
                baseUnitPrices: [],
                basePriceCurrency: position.basePriceCurrency,
                price: 0,
                value: 0,
                discount: 0,
                maxDiscount: accessoriesDiscount?.maxValue,
                maxDiscountType: accessoriesDiscount?.type ?? 'percent',
                baseValue: 0,
                discountType: accessoriesDiscount?.type ?? 'percent',
                edited: false,
                external: false,
            },
        }

        const positionMainDiscount = position.priceDiscounts.find(el => el.type === 'main');
        const positionAccessoriesDiscount = position.priceDiscounts.find(el => el.type === 'accessories')

        let maxDiscountExceeded = false;
        let priceAfterDiscountsValue = 0;

        configuration.priceParts.forEach((part) => {
            let discountToApply = 0;
            let discount = 0;
            let value = 0;
            let baseValue = 0;
            let type: 'percent' | 'value' = 'percent';
            let discountObject = accessoriesDiscount;
            let discountType = 'accessories';
            let positionDiscount = positionAccessoriesDiscount;
            if (part.valueType === 'externalVenetianRaster')  {
                discountObject = mainDiscount;
                discountType = 'main';
                positionDiscount = positionMainDiscount;
            }
            if (part.valueType === 'externalVenetianCoverSegments') {
                const segment = configuration.priceSegments.find((el) => el.id === part.valueId);
                if (segment?.data?.type === 'blind_box') {
                    discountObject = mainDiscount;
                    discountType = 'main';
                    positionDiscount = positionMainDiscount;
                }
            }
            const partPrices: Partial<PricePart> = {
                priceBeforePromotions: 0,
                promotion: null,
                priceBeforeDiscounts: 0,
                discount: null,
                priceAfterDiscounts: 0,
            };
            if (part.unitPrice !== null) {
                partPrices.priceBeforePromotions = TaxRatesService.recalcPrice(
                    TaxRatesService.isGross(document.taxRateCalculationMethod || 'netSummary'),
                    exchange(part.unitPrice, position.basePriceCurrency, document.priceCurrency || 'PLN', side === 'buy' ? buyExchangeRates : exchangeRates),
                    configuration.priceIsGross ?? position.basePriceIsGross,
                    part.taxRate,
                    taxRate?.value ?? position.priceTaxRate
                );

                if (promotion) {
                    partPrices.promotion = {
                        name: promotion.name,
                        promotion: promotion.value,
                        baseValue: partPrices.priceBeforePromotions,
                        value: core.round(partPrices.priceBeforePromotions * promotion.value / 100),
                        type: 'main',
                        promotionType: promotion.type,
                        external: false,
                    };
                    partPrices.priceBeforeDiscounts = partPrices.priceBeforePromotions - partPrices.promotion.value;
                } else {
                    partPrices.priceBeforeDiscounts = partPrices.priceBeforePromotions;
                }
            }
            if (discountObject || positionDiscount) {
                type = discountObject?.type;
                if (positionDiscount?.edited) {
                    type = positionDiscount.discountType;
                }
                if (type) {
                    if (type === 'percent') {
                        discountToApply = positionDiscount?.edited ? positionDiscount.discount : discountObject.value;
                        if (discountObject && side !== 'buy' && discountObject.maxValue && discountObject.type === 'percent' && discountToApply > discountObject.maxValue && !positionDiscount?.external) {
                            if (!canExceedMaxDiscount) {
                                discountToApply = discountObject.maxValue;
                            }
                            maxDiscountExceeded = true;
                        }
                        discount = discountToApply;
                        value = core.round(core.round(partPrices.priceBeforeDiscounts) * discount / 100);
                        baseValue = exchange(value, document.priceCurrency || 'PLN', position.basePriceCurrency, side === 'buy' ? buyExchangeRates : exchangeRates);
                    } else {
                        discountToApply = positionDiscount?.edited ? positionDiscount.value : discountObject.value;
                        if (discountObject && side !== 'buy' && discountObject.maxValue && discountObject.type === 'value' && discountToApply > discountObject.maxValue && !positionDiscount?.external) {
                            if (!canExceedMaxDiscount) {
                                discountToApply = discountObject.maxValue;
                            }
                            maxDiscountExceeded = true;
                        }
                        baseValue = discountToApply;
                        value = exchange(discountToApply, position.basePriceCurrency, document.priceCurrency || 'PLN', side === 'buy' ? buyExchangeRates : exchangeRates);
                        discount = core.round(value / (partPrices.priceBeforeDiscounts) * 100);
                    }
                }
            }
            partPrices.discount = {
                name: positionsDiscounts[discountType].name,
                discount,
                baseValue,
                value,
                type: discountType,
                discountType: type ?? 'value',
                edited: positionDiscount?.edited ?? false,
                external: positionDiscount?.external ?? false,
            };
            if (partPrices.priceBeforeDiscounts !== null) {
                partPrices.priceAfterDiscounts = core.round(core.round(partPrices.priceBeforeDiscounts - value) * part.quantity);
                priceAfterDiscountsValue += partPrices.priceAfterDiscounts;
            }

            positionsDiscounts[discountType].baseUnitPrices.push({
                pricePartValueId: part.valueId,
                pricePartPercentId: part.percentId,
                unitPrice: partPrices.priceBeforeDiscounts,
                quantity: part.quantity,
            });
            positionsDiscounts[discountType].price += partPrices.priceBeforeDiscounts * part.quantity;
            positionsDiscounts[discountType].basePrice += partPrices.priceBeforeDiscounts * part.quantity;

            positionsDiscounts[discountType].price = core.round(positionsDiscounts[discountType].price);
            positionsDiscounts[discountType].basePrice = core.round(positionsDiscounts[discountType].basePrice);

            if (type === 'percent') {
                positionsDiscounts[discountType].value += (value * part.quantity);
                positionsDiscounts[discountType].discount = discount;
                positionsDiscounts[discountType].baseValue += (baseValue * part.quantity);
            } else {
                positionsDiscounts[discountType].value = (value * part.quantity);
                positionsDiscounts[discountType].baseValue = (baseValue * part.quantity);
                positionsDiscounts[discountType].discount = core.round(value / positionsDiscounts[discountType].price * 100);
            }

            positionsDiscounts[discountType].value = core.round(positionsDiscounts[discountType].value);
            positionsDiscounts[discountType].baseValue = core.round(positionsDiscounts[discountType].baseValue);

            positionsDiscounts[discountType].edited = positionsDiscounts[discountType].edited || partPrices.discount.edited;
            positionsDiscounts[discountType].external = positionsDiscounts[discountType].external || partPrices.discount.external;

            if (partPrices.priceBeforePromotions !== null) {
                partPrices.priceBeforePromotions = core.round(partPrices.priceBeforePromotions * part.quantity);
                partPrices.priceBeforeDiscounts = core.round(partPrices.priceBeforeDiscounts * part.quantity);
            }

            Object.assign(part, partPrices);
        });
        return { positionDiscounts: Object.values(positionsDiscounts), maxDiscountExceeded, priceAfterDiscountsValue };
    }

    static formatDiscount(value: number, discountType: Discount['discountType'], lang?: string, currency?: Currency) {
        if (discountType === 'value') {
            return formatCurrency(value, lang, currency);
        } else {
            return core.round(value) + '%';
        }
    }
}
